import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { Button, FormHelp, Input, Label, Spacer, Text, TextButton } from '@zillow/constellation-10';
import { Col, HR, Row } from '@zg-rentals/zrs-comet';
import { emailUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { UmcCompanyOverviewWebApiHandler_verifyEmail } from '@zg-rentals/ts-user-management-client';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import { emitContinueAddingNewUserEvent, EVENT_IDS } from '../../modal/ClickstreamEvents';

type ErrorType = {
  userName?: string;
  email?: string | null;
};

export interface AddUserStepOneTypes {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser?: any;
  onNextStep: () => void;
  onCancel: () => void;
}

const AddUserStepOne = ({ currentUser, onNextStep, onCancel }: AddUserStepOneTypes) => {
  const dispatch = useDispatch();
  const [isVerifyingEmail, setIsVerifyingEmail] = React.useState(false);
  const [errors, setErrors] = React.useState({} as ErrorType);
  const { user } = useRentalManagerUser();
  const supportEmail = emailUtils.getHelpEmail(user.isMultifamilyCustomer, user.isSingleFamilyEnterpriseCustomer);
  const SUPPORT_EMAIL_MSG = `Please contact ${supportEmail} to have this person added to your company.`;

  const handleValidateEmail = () => {
    if (!emailUtils.isValid(currentUser.email)) {
      setErrors({
        ...errors,
        email: 'Please, enter a valid email address',
      });
      return;
    }
    setIsVerifyingEmail(true);
    setErrors({
      ...errors,
      email: 'Verifying...',
    });
    UmcCompanyOverviewWebApiHandler_verifyEmail({ email: currentUser.email })
      .then(() => {
        setIsVerifyingEmail(false);
        setErrors({
          ...errors,
          email: null,
        });
        return;
      })
      .catch((err) => {
        setIsVerifyingEmail(false);
        const message = err.message.toLowerCase().includes('email')
          ? err.message
          : `${err.message} ${SUPPORT_EMAIL_MSG}`;
        setErrors({
          ...errors,
          email: message,
        });
      });
  };

  React.useEffect(() => {
    if (currentUser.email) {
      handleValidateEmail();
    }
  }, []);

  return (
    <div>
      <Spacer css={{ marginBlock: 'sm' }}>
        <Text>Enter basic information</Text>
      </Spacer>
      <Row hidden={[]}>
        <Col md={4}>
          <Label>Email:</Label>
          <Input
            type="text"
            value={currentUser.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              dispatch(UserManagementActions.currentUserSetEmail(e.target.value.trim()))
            }
            onBlur={handleValidateEmail}
            error={!!errors.email}
          />
          {errors.email && (
            <FormHelp error css={{ marginBlockStart: 'xs' }} data-testid="infos-email-error">
              {errors.email}
            </FormHelp>
          )}
        </Col>
      </Row>
      <Row hidden={[]}>
        <Col md={4}>
          <Spacer css={{ marginBlock: 'sm' }}>
            <Label>Name:</Label>
            <Input
              value={currentUser.userName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(UserManagementActions.currentUserSetName(e.target.value))
              }
              error={!!errors.userName}
            />
            {errors.userName && (
              <FormHelp error css={{ marginBlockStart: 'xs' }}>
                {errors.userName}
              </FormHelp>
            )}
          </Spacer>
        </Col>
      </Row>
      <HR />
      <div className="Questions-footer">
        <TextButton className="Questions-button" onClick={onCancel} data-eventid={EVENT_IDS.CANCEL_ADDING_NEW_USER}>
          Cancel
        </TextButton>
        <Button
          tone="brand"
          emphasis="filled"
          onClick={() => {
            emitContinueAddingNewUserEvent({ user });
            onNextStep();
          }}
          disabled={Boolean(isVerifyingEmail || errors.email || !currentUser.email || !currentUser.userName)}
          data-eventid={EVENT_IDS.CONTINUE_ADDING_NEW_USER}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

// @ts-ignore
const mapStateToProps = (state) => ({
  currentUser: state.userManagement.currentUser,
});

// @ts-ignore
export default connect(mapStateToProps)(AddUserStepOne);
